import { useParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Check, Trophy } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { BotonEnlace } from '@/components/Button';
import { Card, Dato, EstadoVacio, Pastilla, Seccion } from '@/components/Card';
import { leerSettings } from '@/db/schema';
import { leerEntrenamiento } from '@/db/entrenamientos';
import { leerRecordsDelEntrenamiento } from '@/db/records';
import { resumenEntrenamiento, textoDuracion, textoSerie, textoVolumen } from '@/domain/entrenamiento';
import { fechaCorta } from '@/domain/hoy';
import { ETIQUETAS_MODALIDAD, modalidadDe, textoPausaYVariante } from '@/domain/modalidad';
import { formatearNumero } from '@/domain/numbers';
import { textoDeRecordNuevo } from '@/domain/records';

/** "la sentadilla", "la sentadilla y el press", "A, B y C". */
function enLista(nombres: readonly string[]): string {
  if (nombres.length <= 1) return nombres[0] ?? '';
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/**
 * Resumen al terminar (Req. 11.10): duración, series hechas y volumen, con la
 * modalidad. En casa también las repeticiones, y el volumen solo si hubo carga.
 * Si el entrenamiento rompió un récord, lo señala (Req. 25.4).
 */
export function ResumenPage() {
  const { id = '' } = useParams();
  const detalle = useLiveQuery(() => leerEntrenamiento(id), [id]);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const records = useLiveQuery(() => leerRecordsDelEntrenamiento(id), [id]);
  if (detalle === undefined || ajustes === undefined || records === undefined) return null;
  if (detalle === null) {
    return (
      <div>
        <PageHeader titulo="No está." volver={{ a: '/gym', texto: 'Gym' }} />
        <EstadoVacio titulo="Ese entrenamiento ya no existe." />
      </div>
    );
  }
  const { workout, ejercicios } = detalle;
  const r = resumenEntrenamiento(workout, ejercicios.flatMap((e) => e.series));
  const casa = modalidadDe(workout) === 'casa';
  const conRecord = new Set(records.porEjercicio.map((e) => e.exerciseId));

  return (
    <div className="space-y-6">
      <PageHeader
        antetitulo={`${fechaCorta(workout.fecha)} · ${ETIQUETAS_MODALIDAD[modalidadDe(workout)]}`}
        titulo="Entrenamiento terminado."
        descripcion={workout.nombre}
        volver={{ a: '/gym', texto: 'Gym', etiqueta: 'Volver a Gym' }}
      />
      <Card>
        <dl className="divide-y divide-borde">
          <Dato etiqueta="Duración" valor={textoDuracion(r.duracionSeg)} />
          <Dato
            etiqueta="Series hechas"
            valor={String(r.seriesHechas)}
            detalle={r.calentamientos > 0 ? `Más ${r.calentamientos} de calentamiento, que no cuentan.` : undefined}
          />
          {casa && <Dato etiqueta="Repeticiones" valor={formatearNumero(r.repeticiones, { decimales: 0 })} />}
          {(!casa || r.volumenKg > 0) && (
            <Dato
              etiqueta="Volumen"
              valor={textoVolumen(r.volumenKg, ajustes.unidadPeso)}
              detalle={[
                'Carga × repeticiones; con mancuernas, el peso de cada una.',
                r.seriesEnPlacas > 0 ? `${r.seriesEnPlacas} series en placas no entran: cada máquina es distinta.` : null,
              ]
                .filter(Boolean)
                .join(' ')}
            />
          )}
        </dl>
      </Card>

      {/* Récords nuevos (Req. 25.4): solo si superaron lo de las sesiones anteriores */}
      {records.total > 0 && (
        <Banner
          tono="exito"
          titulo={records.total === 1 ? 'Rompiste un récord' : `Rompiste ${records.total} récords`}
        >
          <ul className="space-y-1">
            {records.porEjercicio.map((e) => (
              <li key={e.exerciseId}>
                <span className="font-medium text-texto">{e.nombre}</span>:{' '}
                {e.records.map((rn) => textoDeRecordNuevo(rn, e.unidad)).join('; ')}.
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {records.primeraVez.length > 0 && (
        <p className="text-[12px] leading-relaxed text-texto-suave">
          Primera vez con {enLista(records.primeraVez)}: todavía no hay récord porque no hay otra sesión con qué
          comparar. Desde la próxima ya se puede.
        </p>
      )}

      <Seccion titulo="Lo que hiciste">
        <ul className="divide-y divide-borde">
          {ejercicios.map((e) => {
            const detalleCasa = textoPausaYVariante(e.we);
            return (
              <li key={e.we.id} className="py-3">
                <p className="flex items-center gap-2 text-[14px] font-medium text-texto">
                  {e.ejercicio?.nombre ?? 'Ejercicio'}
                  {e.ejercicio !== undefined && conRecord.has(e.ejercicio.id) && (
                    <Pastilla>
                      <Trophy className="size-3" />
                      Récord
                    </Pastilla>
                  )}
                </p>
                <p className="text-[12px] text-texto-suave">{e.series.map((s) => textoSerie(s)).join(', ')}</p>
                {detalleCasa !== '' && <p className="text-[12px] text-texto-suave">{detalleCasa}</p>}
              </li>
            );
          })}
        </ul>
      </Seccion>
      {workout.notas !== null && <p className="text-[13px] leading-relaxed text-texto-suave">{workout.notas}</p>}
      <BotonEnlace to="/gym" icono={Check}>
        Listo
      </BotonEnlace>
    </div>
  );
}
